import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Paper,
  Divider,
  Tab,
  Tabs
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LinkIcon from '@mui/icons-material/Link';
import ImageUpload from '../ImageUpload/ImageUpload';
import { useTranslation } from 'react-i18next';
import { adminService } from '../../../api/services/admin';

interface ImageInfo {
  id: string | number | null;
  url: string;
}

interface ThumbnailChooserProps {
  value: ImageInfo;
  onChange: (image: ImageInfo) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`thumbnail-tabpanel-${index}`}
      aria-labelledby={`thumbnail-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `thumbnail-tab-${index}`,
    'aria-controls': `thumbnail-tabpanel-${index}`,
  };
}

export default function ThumbnailChooser({ value, onChange }: ThumbnailChooserProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [urlInput, setUrlInput] = useState(value?.url || '');
  const [selectedImage, setSelectedImage] = useState<ImageInfo | null>(value?.url ? value : null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // При изменении value обновляем внутреннее состояние
  React.useEffect(() => {
    if (value?.url) {
      setUrlInput(value.url);
      setSelectedImage(value);
    }
  }, [value]);

  const handleOpen = () => {
    setOpen(true);
    // Установка URL из текущего значения, если он есть
    if (value && value.url) {
      setUrlInput(value.url);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
    setUrlInput('');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSelectImage = async (image: any) => {
    console.log('Выбрано изображение:', image);

    // Немедленное применение выбранного изображения
    if (image) {
      const newImage = {
        id: image.id || null,
        url: image.url || ''
      };

      setSelectedImage(newImage);

      // Применение изображения сразу при выборе
      onChange(newImage);

      if (image.id) {
        setPreviewLoading(true);
        try {
          // Загрузка превью изображения
          const response = await adminService.loadImagePreview(image.id);

          if (response && response.status === 200 && response.data) {
            // Создаем Blob из данных ответа
            const blob = new Blob([response.data], {type: response.headers['content-type'] || 'image/jpeg'});
            const imageUrl = URL.createObjectURL(blob);

            // Обновляем URL изображения с полученным превью
            const updatedImage = {
              id: image.id,
              url: imageUrl
            };

            setSelectedImage(updatedImage);
            // Повторное применение с актуальным URL
            onChange(updatedImage);
            console.log('Изображение загружено, URL установлен:', imageUrl);
          }
        } catch (error) {
          console.error('Ошибка при загрузке превью:', error);
        } finally {
          setPreviewLoading(false);
        }
      }
    }
  };

  const handleConfirm = () => {
    console.log('Подтверждение выбора, tabValue:', tabValue);

    if (tabValue === 0 && selectedImage) {
      // Режим выбора из библиотеки
      console.log('Подтверждаем выбор изображения из библиотеки:', selectedImage);

      // Немедленное применение выбранного изображения
      const newImage = {
        id: selectedImage.id,
        url: selectedImage.url
      };

      // Проверяем, что URL изображения доступен
      const img = new Image();
      img.onload = () => {
        console.log('Изображение успешно загружено:', selectedImage.url);
        onChange(newImage);
      };
      img.onerror = () => {
        console.error('Ошибка загрузки изображения:', selectedImage.url);
        // Применяем даже с ошибкой, но логируем
        onChange(newImage);
      };
      img.src = selectedImage.url;

    } else if (tabValue === 1 && urlInput) {
      // Режим URL
      console.log('Подтверждаем выбор изображения по URL:', urlInput);

      // Создаем новый объект изображения для избежания проблем с ссылками
      const newImage = {
        id: null,
        url: urlInput
      };

      // Проверяем валидность URL перед применением
      const img = new Image();
      img.onload = () => {
        console.log('URL изображение успешно загружено:', urlInput);
        onChange(newImage);
      };
      img.onerror = () => {
        console.error('Ошибка загрузки URL изображения:', urlInput);
        // Применяем даже с ошибкой, но логируем
        onChange(newImage);
      };
      img.src = urlInput;
    }

    // Закрываем диалог после применения изображения
    handleClose();
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 2, position: 'relative' }}>
        <Typography variant="subtitle1" gutterBottom>
          {t("cabinet.mdx_editor.thumbnail")}
        </Typography>

        {value && value.url ? (
          <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
            <img 
              src={value.url} 
              alt="Thumbnail" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '200px', 
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '4px'
              }}
              onError={(e) => {
                console.error('Ошибка загрузки изображения:', value.url);
                (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDEzLjUgMTUgMTAgMjAiPjwvcG9seWxpbmU+PC9zdmc+';
              }}
            />
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              ID: {value.id ? String(value.id) : 'URL только'}
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              sx={{ mt: 1 }}
              onClick={handleOpen}
              startIcon={<ImageIcon />}
            >
              {t("cabinet.mdx_editor.change_thumbnail")}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, border: '2px dashed #ddd', borderRadius: '4px' }}>
            <AddPhotoAlternateIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" gutterBottom color="text.secondary">
              {t("cabinet.mdx_editor.no_thumbnail")}
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleOpen}
              startIcon={<ImageIcon />}
            >
              {t("cabinet.mdx_editor.add_thumbnail")}
            </Button>
          </Box>
        )}
      </Paper>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{t("cabinet.mdx_editor.choose_thumbnail")}</DialogTitle>
        <Divider />

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="thumbnail source tabs">
            <Tab label={t("cabinet.mdx_editor.from_library")} {...a11yProps(0)} />
            <Tab label={t("cabinet.mdx_editor.from_url")} {...a11yProps(1)} />
          </Tabs>
        </Box>

        <DialogContent sx={{ minHeight: '500px' }}>
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ height: '500px', overflow: 'hidden' }}>
              <ImageUpload onSelectImage={handleSelectImage} />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("cabinet.mdx_editor.image_url")}
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  InputProps={{
                    startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>

              {urlInput && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("cabinet.mdx_editor.preview")}
                  </Typography>
                  <Box sx={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '4px', 
                    p: 1, 
                    display: 'flex', 
                    justifyContent: 'center',
                    minHeight: '200px',
                    alignItems: 'center'
                  }}>
                    <img 
                      src={urlInput} 
                      alt="Preview" 
                      style={{ maxWidth: '100%', maxHeight: '300px' }} 
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDEzLjUgMTUgMTAgMjAiPjwvcG9seWxpbmU+PC9zdmc+';
                        (e.target as HTMLImageElement).alt = 'Invalid image';
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            {t("cabinet.mdx_editor.cancel")}
          </Button>
          <Button 
            onClick={handleConfirm} 
            color="primary" 
            variant="contained"
          >
            {t("cabinet.mdx_editor.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
